import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Coins, Mail, Phone, Search } from 'lucide-react';
import T from 'Components/Helpers/translate';
import {
	fetchStoredLookup,
	PROVIDER_NAMES,
	runLookup,
	STATUS_KEYS,
	type LookupChannel,
	type LookupResult,
	type LookupValue
} from 'App/shadcn/pages/ProspectingTools/Lookup/lookupApi';
import { sourceOf } from 'App/shadcn/pages/ProspectingTools/Lookup/LookupParts';
import { formatCredits } from 'App/shadcn/pages/AIWorkspace/Admin/workspaceSettingsShared';
import FillExistingContact from './FillExistingContact';

export type FoundContactDetails = { email?: string; phone?: string };

/**
 * Each channel holds its own lookup's answer: what it found, or why not. Another channel's answer
 * never changes it, so "out of credits" stays that, and an email found by the phone's lookup is not
 * shown in a row whose own lookup said otherwise (WEB-15192).
 */
type ChannelState = { run: 'idle' | 'finding' | 'answered'; value?: LookupValue; reason?: string };

type Card = {
	channels: Record<LookupChannel, ChannelState>;
	aiCredits: number;
	outsideEuOwnKeys: NonNullable<LookupResult['outsideEuOwnKeys']>;
	/** When the rep's stored lookup the card opened with was found (WEB-15185). */
	storedAt?: string;
};

const EMPTY: Card = {
	channels: { email: { run: 'idle' }, phoneNumber: { run: 'idle' } },
	aiCredits: 0,
	outsideEuOwnKeys: []
};

// Reasons that may say something else next time: the lookup can be asked again from the same button.
const RETRY = [STATUS_KEYS.failed, STATUS_KEYS.outOfCredits, STATUS_KEYS.noConnectors];

// How long the buttons wait for the stored lookup before they are offered without it (WEB-15185).
const STORED_WAIT_MS = 3000;

const CHANNELS: Array<{
	channel: LookupChannel;
	find: string;
	label: string;
	notFound: string;
	Icon: typeof Mail;
}> = [
	{
		channel: 'email',
		Icon: Mail,
		find: 'assistant.embedPersonFindContact',
		label: 'prospectingTools.lookup.channel.email',
		notFound: 'prospectingTools.lookup.emailNotFound'
	},
	{
		channel: 'phoneNumber',
		Icon: Phone,
		find: 'prospectingTools.lookup.findPhoneNumber',
		label: 'voice.phoneNumber',
		notFound: 'prospectingTools.lookup.phoneNotFound'
	}
];

/**
 * One channel's row on the card: what was found, that nothing was, or where it will show (WEB-15175).
 * Always one line, so nothing below moves; what doesn't fit is read on hover (WEB-15192).
 */
const ChannelRow: React.FC<{ Icon: typeof Mail; text: string; tone?: 'muted' | 'error' }> = ({ Icon, text, tone }) => (
	<div className={`embed-add-channel${tone ? ` is-${tone}` : ''}`}>
		<Icon aria-hidden />
		<span className="embed-add-channel-value" title={text}>
			{text}
		</span>
	</div>
);

const valueText = (found: LookupValue) => `${found.value} · ${sourceOf(found)}`;

type Props = {
	linkedinUrl: string;
	/** What was found, for the contact about to be added. Called again with `{}` for a new profile. */
	onFound: (details: FoundContactDetails) => void;
	/** The CRM contact this profile already is, if any: then what was found can be added to it (WEB-13446). */
	existingContactId?: number;
	/**
	 * What that contact already has: not offered for lookup, since it would spend credits for nothing (WEB-13771).
	 * Read once per profile: the card is mounted only after the CRM answered.
	 */
	inCrm?: Record<LookupChannel, boolean>;
	/** After "Add to the contact" saved, so the card can show the contact's new values. */
	onFilled?: () => void;
};

/**
 * "Find email" and "Find phone number" on a LinkedIn profile in the extension's Add person tab
 * (WEB-13374). The same Lookup as supersearch's, so Airscale first under AIRSCALE_INTEGRATION and the
 * customer's waterfall otherwise, charged the same way. Each runs only when the rep asks: the phone
 * costs about 20 times the email (WEB-13365). The profile is already on screen, so both are offered
 * from the start, in either order, and what each finds lands on one card (WEB-13771). Both in one
 * lookup is the main button, like Lusha's, with each alone beside it (WEB-15175).
 */
const FindContactDetails: React.FC<Props> = ({ linkedinUrl, onFound, existingContactId, inCrm, onFilled }) => {
	const [card, setCard] = useState(EMPTY);
	// The profile on screen: an answer for one the rep has since left is dropped.
	const shown = useRef(linkedinUrl);
	// The profile whose stored lookup has been read, or waited for long enough. Until then the buttons
	// wait, so they do not move when it answers (WEB-15185).
	const [storedFor, setStoredFor] = useState<string | null>(null);

	useEffect(() => {
		shown.current = linkedinUrl;
		setCard(EMPTY);
		setStoredFor(null);
		onFound({});
		// The contact already has both (the parent holds the card until the CRM said): nothing to show.
		if (inCrm?.email && inCrm.phoneNumber) {
			setStoredFor(linkedinUrl);
			return;
		}
		// What the rep already paid for shows as the card opens: read from their stored lookup, with no
		// provider asked. The channels it never asked stay offered (WEB-15185).
		const settle = () => {
			if (shown.current === linkedinUrl) setStoredFor(linkedinUrl);
		};
		// Without it the buttons are offered as before: a lookup then answers from the stored one anyway.
		const timer = setTimeout(settle, STORED_WAIT_MS);
		fetchStoredLookup(linkedinUrl)
			.then(stored => {
				const storedPerson = stored?.result.person;
				if (shown.current !== linkedinUrl || !stored || !storedPerson) return;
				setCard(current => {
					const channels = { ...current.channels };
					for (const channel of stored.channels) {
						// After a slow read the rep may have run a lookup: one running, or one that found it, stays.
						if (channels[channel].run === 'finding' || channels[channel].value) continue;
						const value = storedPerson[channel] ?? undefined;
						channels[channel] = value
							? { run: 'answered', value }
							: { run: 'answered', reason: CHANNELS.find(c => c.channel === channel)?.notFound };
					}
					return { ...current, channels, storedAt: stored.result.storedAt };
				});
			})
			.catch((err: unknown) => console.error('[EmbedAIChat] reading the stored lookup failed', { err }))
			.finally(() => {
				clearTimeout(timer);
				settle();
			});
		return () => clearTimeout(timer);
	}, [linkedinUrl, onFound]);

	const setChannels = (channels: LookupChannel[], state: (channel: LookupChannel) => ChannelState) =>
		setCard(current => ({
			...current,
			channels: { ...current.channels, ...Object.fromEntries(channels.map(channel => [channel, state(channel)])) }
		}));

	// Both channels in one lookup is the default, as on Lusha; each alone stays one click away (WEB-15175).
	const find = (channels: LookupChannel[]) => {
		// A retry asks the providers again: a stored answer (skipped for credits, say) would only repeat itself.
		const retry = channels.some(channel => card.channels[channel].run === 'answered');
		setChannels(channels, () => ({ run: 'finding' }));
		runLookup(linkedinUrl, channels, retry)
			.then(answer => {
				if (shown.current !== linkedinUrl) return;
				// No one found at all says what each row would: "No email found", not supersearch's generic
				// "Nothing matched"; any other reason is the lookup's own.
				const reason =
					answer.person || answer.status === 'found' || answer.status === 'notFound'
						? null
						: STATUS_KEYS[answer.status];
				setChannels(channels, channel => {
					const value = answer.person?.[channel] ?? undefined;
					return value
						? { run: 'answered', value }
						: {
								run: 'answered',
								// Providers skipped for credits may have it: said so, and asked again later.
								reason: answer.skippedForCredits
									? STATUS_KEYS.outOfCredits
									: reason ?? CHANNELS.find(c => c.channel === channel)?.notFound
						  };
				});
				setCard(current => ({
					...current,
					aiCredits: current.aiCredits + answer.aiCredits,
					outsideEuOwnKeys: [
						...current.outsideEuOwnKeys,
						...(answer.outsideEuOwnKeys ?? []).filter(key => !current.outsideEuOwnKeys.includes(key))
					]
				}));
			})
			.catch((err: unknown) => {
				console.error('[EmbedAIChat] contact lookup failed', { channels, err });
				if (shown.current === linkedinUrl)
					setChannels(channels, () => ({ run: 'answered', reason: STATUS_KEYS.failed }));
			});
	};

	const email = card.channels.email.value?.value;
	const phone = card.channels.phoneNumber.value?.value;

	useEffect(() => {
		if (email || phone) onFound({ email, phone });
	}, [email, phone, onFound]);

	// What the CRM lacks is offered for good: a button that answered stays where it was, off, so the
	// next click never lands on the other lookup, nor does anything below move - also once "Add to the
	// contact" put it in the CRM (WEB-15192).
	const offered = CHANNELS.filter(({ channel }) => !inCrm?.[channel] || card.channels[channel].run !== 'idle');
	const canFind = (channel: LookupChannel) => {
		const { run, reason } = card.channels[channel];
		return run === 'idle' || (run === 'answered' && reason !== undefined && RETRY.includes(reason));
	};
	const finding = (channel: LookupChannel) => card.channels[channel].run === 'finding';

	if (storedFor !== linkedinUrl) {
		return (
			<div className="embed-add-settling" aria-busy="true">
				<span className="embed-add-settling-find" />
			</div>
		);
	}

	return (
		<div className="embed-add-fields">
			{offered.length ? (
				// What it cost sits beside the credits notice, so it takes no line of its own (WEB-15192).
				<div className="embed-add-credits-row">
					<span className="embed-add-uses-credits">
						<Coins aria-hidden />
						<span>{T('prospectingTools.lookup.usesCredits')}</span>
					</span>
					{card.aiCredits > 0 ? (
						<span className="embed-add-credits">
							<Coins aria-hidden />
							{T('prospectingTools.lookup.aiCredits', { count: formatCredits(card.aiCredits) })}
						</span>
					) : card.storedAt ? (
						// Shown from the rep's stored lookup, so nothing was paid this time (WEB-15185).
						<span className="embed-add-fv-label">
							{T('prospectingTools.lookup.stored', { date: moment(card.storedAt).format('L') })}
						</span>
					) : null}
				</div>
			) : null}
			{offered.length === 2 ? (
				<div className="embed-add-find">
					<button
						type="button"
						className="embed-add-btn"
						disabled={!offered.every(({ channel }) => canFind(channel))}
						onClick={() => find(offered.map(({ channel }) => channel))}
					>
						<Search />
						{/* Busy only when it is the one running: one channel alone keeps its label (WEB-15175). */}
						{offered.every(({ channel }) => finding(channel))
							? T('prospectingTools.lookup.loading')
							: T('assistant.embedPersonFindBoth')}
					</button>
					{offered.map(({ channel, find: findKey, Icon }) => (
						<button
							key={channel}
							type="button"
							className="embed-add-btn embed-add-btn-icon"
							title={T(findKey)}
							aria-label={T(findKey)}
							disabled={!canFind(channel)}
							onClick={() => find([channel])}
						>
							<Icon />
						</button>
					))}
				</div>
			) : offered.length === 1 ? (
				<div className="embed-add-find">
					{offered.map(({ channel, find: findKey }) => (
						<button
							key={channel}
							type="button"
							className="embed-add-btn"
							disabled={!canFind(channel)}
							onClick={() => find([channel])}
						>
							<Search />
							{finding(channel) ? T('prospectingTools.lookup.loading') : T(findKey)}
						</button>
					))}
				</div>
			) : null}
			{/* Each answer lands in its channel's own row, never on a new line (WEB-15192). */}
			{offered.map(({ channel, label, Icon }) => {
				const { value, reason } = card.channels[channel];
				return value ? (
					<ChannelRow key={channel} Icon={Icon} text={valueText(value)} />
				) : (
					<ChannelRow
						key={channel}
						Icon={Icon}
						text={T(reason ?? label)}
						tone={reason === STATUS_KEYS.failed ? 'error' : 'muted'}
					/>
				);
			})}
			{card.outsideEuOwnKeys.length ? (
				<span className="embed-add-fv-label">
					{T('prospectingTools.lookup.outsideEuOwnKeys', {
						providers: card.outsideEuOwnKeys.map(provider => PROVIDER_NAMES[provider]).join(', ')
					})}
				</span>
			) : null}
			{/* Held, off, until something is found, so "Open" below never moves down (WEB-15192). */}
			{existingContactId && offered.length ? (
				<FillExistingContact
					// Something found after a fill (the other channel) offers the fill again.
					key={`${email ?? ''}|${phone ?? ''}`}
					contactId={existingContactId}
					// Only what the contact lacked, so a find it already has never offers a fill for nothing.
					found={{ email: inCrm?.email ? undefined : email, phone: inCrm?.phoneNumber ? undefined : phone }}
					onFilled={onFilled}
				/>
			) : null}
		</div>
	);
};

export default FindContactDetails;
